(ns manage.slate-editor.macro-node
  (:require ["@mui/material/Chip$default" :as Chip]
            ["slate" :as slate]
            ["slate-react" :as slate-react]
            [clojure.string :as str]
            [common.util :refer [spread-props]]
            [manage.slate-editor.common :as common]
            [manage.slate-editor.debug :as debug]
            [manage.slate-editor.util :as util :refer [with-ref]]
            [mate.core :as mc]
            [uix.core :refer [defui $]]))

(defn- is-valid-macro-content-node? [^js node]
  (let [type (.-type node)
        children (.-children node)]
    (and (= type common/macro-content-node-type)
         (= (count children) 1)
         (some? (re-matches common/macro-content-node-regex (-> children (aget 0) .-text))))))

(defn- is-valid-macro-node? [^js node]
  (let [type (.-type node)
        children (.-children node)]
    (and (= type common/macro-node-type)
         (= (count children) 3)
         (let [[left-child center-child right-child] children]
           (and (= (.-text left-child) "${")
                (is-valid-macro-content-node? center-child)
                (= (.-text right-child) "}"))))))

(defn- unwrap-invalid-macro-nodes [^js editor ^js node-entry original-normalize-node]
  (let [[node path] node-entry]
    (if (and (= (.-type node) common/macro-node-type)
             (not (is-valid-macro-node? node)))
      (util/flatten-node editor path)
      (original-normalize-node node-entry))))

(defn- wrap-macro-nodes [^js editor ^js node-entry original-normalize-node]
  (let [[node path] node-entry]
    (if (and (slate/Text.isText node)
             (not (util/is-descendant-of-node-type? editor path common/macro-node-type)))
      (if-some [[[macro-index macro-text]
                 [macro-content-index macro-content-text]] (mc/re-find-indexed (common/macro-node-regex) (.-text node))]
        (with-ref [^js macro-content-start-ref (slate/Editor.pointRef editor
                                                                      #js {:path path
                                                                           :offset macro-content-index}
                                                                      #js {:affinity "forward"})
                   ^js macro-content-end-ref (slate/Editor.pointRef editor
                                                                    #js {:path path
                                                                         :offset (+ macro-content-index (count macro-content-text))}
                                                                    #js {:affinity "forward"})]
          (slate/Transforms.wrapNodes editor
                                      #js {:type common/macro-node-type}
                                      #js {:at   #js {:anchor #js {:path   path
                                                                   :offset macro-index}
                                                      :focus #js {:path   path
                                                                  :offset (+ macro-index (count macro-text))}}
                                           :split true})
          (if (slate/Point.equals (.-current macro-content-start-ref)
                                  (.-current macro-content-end-ref))
            (slate/Transforms.insertNodes editor
                                          #js {:type common/macro-content-node-type
                                               :children #js [#js {:text ""}]}
                                          #js {:at    (.-current macro-content-start-ref)
                                               :split true})
            (slate/Transforms.wrapNodes editor
                                        #js {:type common/macro-content-node-type}
                                        #js {:at   #js {:anchor (.-current macro-content-start-ref)
                                                        :focus (.-current macro-content-end-ref)}
                                             :split true})))
        (original-normalize-node node-entry))
      (original-normalize-node node-entry))))

(defn- set-is-valid-flag
  "Set a 'mark' on the macro node to identify if the content text is a valid macro or not."
  [is-macro-valid?]
  (fn [^js editor ^js node-entry original-normalize-node]
    (let [[^js node node-path] node-entry
          is-a-macro (= (.-type node) common/macro-node-type)]
      (when is-a-macro
        (let [old-is-valid (.-isValid node)
              _macro-text (slate/Node.string node)
              macro-content-node (-> node .-children (aget 1))
              macro-content-text (slate/Node.string macro-content-node)
              new-is-valid (is-macro-valid? macro-content-text)]
          ;; Only set the node when the mark is different, to avoid an infinite loop during the node normalization.
          (when (not= old-is-valid new-is-valid)
            (slate/Transforms.setNodes editor
                                       #js {:isValid new-is-valid}
                                       #js {:at node-path})))))
    (original-normalize-node node-entry)))

(defn with-macro-node-support
  [^js editor is-macro-valid?]
  (-> editor
      (util/with-inline-element-types [common/macro-node-type
                                       common/macro-content-node-type])

      ;; This runs in third (after the second)
      (util/with-normalize-node (set-is-valid-flag is-macro-valid?))
      ;; This runs in second (after the first)
      (util/with-normalize-node wrap-macro-nodes)
      ;; This runs first
      (util/with-normalize-node unwrap-invalid-macro-nodes)
      #_(util/with-normalize-node debug/log-normalize-node-calls)))

(defn auto-insert-macro-closing-curly-bracket
  [^js editor ^js event]
  (when (= (.-key event) "{")
    (let [selection (.-selection editor)]
      (when (some-> selection slate/Range.isCollapsed)
        (let [cursor-point                 (slate/Editor.start editor selection)
              one-char-before-cursor-point (slate/Editor.before editor cursor-point #js {:unit "character"})
              char-before                  (when (some? one-char-before-cursor-point)
                                             (slate/Editor.string editor #js {:anchor one-char-before-cursor-point
                                                                              :focus  cursor-point}))]
          (when (= char-before "$")
            (let [[cursor-node cursor-node-path] (slate/Editor.node editor cursor-point)
                  text-around                    (->> (util/find-next-node-entries-while editor cursor-node-path slate/Text.isText)
                                                      (map (fn [[node _path]] node))
                                                      (cons cursor-node)
                                                      (map (fn [node] (.-text node)))
                                                      (apply str))
                  text-starting-from-dollar-sign (subs text-around (.-offset cursor-point))
                  has-closing-curly-bracket      (str/includes? text-starting-from-dollar-sign "}")]
              (when-not has-closing-curly-bracket
                (slate/Transforms.insertText editor "}")
                (slate/Transforms.move editor #js {:distance 1
                                                   :unit "character"
                                                   :reverse true})))))))))

(defn skip-over-macro-closing-curly-bracket
  [^js editor ^js event]
  (when (= (.-key event) "}")
    (let [selection (.-selection editor)]
      (when (some-> selection slate/Range.isCollapsed)
        (let [cursor-point                (slate/Editor.start editor selection)
              one-char-after-cursor-point (slate/Editor.after editor cursor-point #js {:unit "character"})
              char-after                  (when (some? one-char-after-cursor-point)
                                            (slate/Editor.string editor #js {:anchor cursor-point
                                                                             :focus  one-char-after-cursor-point}))]
          (when (= char-after "}")
            (when-some [[_macro-node macro-node-path] (util/find-node-path-above editor cursor-point common/macro-node-type)]
              (when (slate/Editor.isEnd editor one-char-after-cursor-point macro-node-path)
                (.preventDefault event)
                (slate/Transforms.move editor #js {:distance 1
                                                   :unit "character"})))))))))

(defn get-macro-node-under-cursor
  [^js editor]
  (when-some [cursor-point (util/cursor-point-when-no-selection editor)]
    (when-some [[macro-node _macro-path] (util/find-node-path-above editor cursor-point common/macro-node-type)]
      macro-node)))

(defn get-macro-content-text
  [^js macro-node]
  (-> macro-node
      .-children (aget 1)
      .-children (aget 0)
      .-text))

(defn cursor-on-inner-macro-text?
  "Returns true if and only if the cursor is inside a macro node, between '${' and '}'."
  [^js editor]
  (if-some [cursor-point (util/cursor-point-when-no-selection editor)]
    (if-some [[_macro-node macro-path] (util/find-node-path-above editor cursor-point common/macro-node-type)]
      (let [after-opener-point (slate/Editor.end editor (util/get-child-path macro-path 0))     ; after "${"
            before-closer-point (slate/Editor.start editor (util/get-child-path macro-path 2))] ; before "}"
        (slate/Range.includes #js {:anchor after-opener-point
                                   :focus before-closer-point}
                              cursor-point))
      false)
    false))

(defn insert-macro-part [^js editor new-text]
  ;; Find where the last part of the macro starts and ends.
  (let [cursor-point (util/cursor-point-when-no-selection editor)
        [macro-node macro-path] (util/find-node-path-above editor cursor-point common/macro-node-type)
        macro-content-text (get-macro-content-text macro-node)
        last-part-start (if-some [last-breakpoint-index (max (str/last-index-of macro-content-text ".")
                                                             (str/last-index-of macro-content-text ":"))]
                          (inc last-breakpoint-index)
                          0)
        last-part-end (count macro-content-text)
        macro-content-text-path (-> macro-path
                                    (util/get-child-path 1)
                                    (util/get-child-path 0))]
    ;; Replace the last part with the new text
    (slate/Transforms.insertText editor new-text #js {:at #js {:anchor #js {:path   macro-content-text-path
                                                                            :offset last-part-start}
                                                               :focus  #js {:path   macro-content-text-path
                                                                            :offset last-part-end}}})))

(defui macro-content-component [{:keys [attributes children]}]
  ($ :span attributes children))

(defui macro-component [{:keys [valid attributes children]}]
  (let [focused (slate-react/useFocused)
        selected (slate-react/useSelected)]
    ($ Chip (spread-props {:component "span"
                           :label children
                           :sx #js {:fontSize "inherit"
                                    :fontFamily "'Roboto Mono', monospace"}
                           :color (cond
                                    (and focused selected) "default"
                                    valid "success"
                                    (not valid) "error")}
                          attributes))))
